import type { UseMutationResult } from '@tanstack/react-query';
import type { TChangeRoleCarrier } from 'src/types/auth';
import type {
  TEditProfileBody,
  TUserGroupsResponse,
  TUserInfoResponse,
} from 'src/types/user';

import { useMutation, useQuery } from '@tanstack/react-query';
import {
  UserGroupResponseSchema,
  UserResponseSchema,
} from 'src/schema/user-schema';

import { instance } from './instance';

const getUserInfo = async (): Promise<TUserInfoResponse> => {
  const { data } = await instance.get(`/api/panel/authsystem/v1.0/user/info`);
  const result = UserResponseSchema.safeParse(data);
  if (!result.success) {
    throw new Error(`Parse User Response Error${result.error.toString()}`);
  }
  return data;
};
const getUserGroups = async (): Promise<TUserGroupsResponse> => {
  const { data } = await instance.get(`/api/panel/authsystem/v1.0/user/groups`);
  const result = UserGroupResponseSchema.safeParse(data);

  if (!result.success) {
    throw new Error(`Parse User Groups Error${result.error.toString()}`);
  }
  return data;
};
const setUserCurrentInfo = async ({
  currentType,
  currentId,
}: {
  currentType: string | null;
  currentId: number | null;
}): Promise<TChangeRoleCarrier> => {
  const { data } = await instance.get(
    `/api/panel/authsystem/v1.0/user/set_currentid?current_id=${currentId ?? ''}&current_type=${currentType ?? ''}`,
  );
  return data;
};

const editProfileInfo = async (
  body: TEditProfileBody,
): Promise<TGlobalResponse<string>> => {
  const { data } = await instance.put(
    `/api/panel/authsystem/v1.0/user/update-profile`,
    body,
  );
  return data;
};
function useGetUserInfo({ enabled = true }: { enabled?: boolean }) {
  // const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['user-info'],
    queryFn: () => getUserInfo(),
    enabled,
  });
}

function useGetUserGroups({ enabled = true }: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['user-groups'],
    queryFn: () => getUserGroups(),
    enabled,
  });
}

function useGetSetCurrentInfo({
  enabled = true,
  currentType,
  currentId,
}: {
  enabled?: boolean;
  currentType: string | null;
  currentId: number | null;
}) {
  return useQuery({
    queryKey: ['user-current-info', currentId, currentType],
    queryFn: () => setUserCurrentInfo({ currentId, currentType }),
    enabled,
  });
}
const useEditProfileInfo = (): UseMutationResult<
  TGlobalResponse<string>,
  unknown,
  { body: TEditProfileBody }
> =>
  useMutation({
    mutationFn: ({ body }: { body: TEditProfileBody }) => editProfileInfo(body),
  });

export {
  useEditProfileInfo,
  useGetSetCurrentInfo,
  useGetUserGroups,
  useGetUserInfo,
};
